// Translation dictionary for Life Leverage OS (English + Polish + Vietnamese).
// Vietnamese lives in ./vi.js (same structure, 1:1 key parity).
// IMPORTANT: enum VALUES (categories, statuses, priorities, finance types,
// frequencies) are stored in the database and used in filter logic / chip color
// maps. We therefore translate ONLY their display labels via the `enums` map and
// never the underlying canonical English value.

import { vi } from '@/i18n/vi';

export const translations = {
  en: {
    common: {
      save: 'Save',
      saveChanges: 'Save changes',
      cancel: 'Cancel',
      delete: 'Delete',
      remove: 'Remove',
      edit: 'Edit',
      open: 'Open',
      add: 'Add',
      none: 'None',
      select: 'Select',
      progress: 'Progress',
      next: 'Next:',
      cannotUndo: 'This cannot be undone.',
      loading: 'Loading…',
    },
    nav: {
      dashboard: 'Today',
      goals: 'Goals',
      tasks: 'Tasks',
      projects: 'Projects',
      ai: 'Assistant',
    },
    more: {
      finance: 'Finance',
      habits: 'Habits',
      review: 'Daily Review',
      settings: 'Settings',
    },
    screens: {
      dashboard: 'Today',
      goals: 'Goals',
      tasks: 'Tasks',
      projects: 'Projects',
      finance: 'Finance',
      habits: 'Habits',
      review: 'Daily Review',
      settings: 'Settings',
    },
    language: {
      label: 'Language',
      switch: 'Switch language',
    },
    legal: {
      notice: '',
      backToApp: 'Back to app / Wróć do aplikacji',
      website: 'Website:',
      contact: 'Contact:',
    },
    onboarding: {
      title: 'Turn chaos into a clear plan for today.',
      subtitle: 'Your personal command center for goals, tasks, money, habits and daily action.',
      chipGoals: 'Goals',
      chipMoney: 'Money',
      chipHabits: 'Habits',
      getStarted: 'Get Started',
      demo: 'Continue as Demo User',
      tagline: 'Calm. Focused. Built to help you know your next step.',
      demoWelcome: 'Welcome, Marek — your demo workspace is ready.',
      demoError: 'Could not start demo. Please try again.',
    },
    auth: {
      signupTitle: 'Create your account',
      loginTitle: 'Welcome back',
      signupSubtitle: 'Start organizing your life in one calm place.',
      loginSubtitle: 'Log in to continue your command center.',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'you@example.com',
      password: 'Password',
      createAccount: 'Create account',
      login: 'Log in',
      or: 'or',
      demo: 'Continue as Demo User',
      haveAccount: 'Already have an account?',
      noAccount: "Don't have an account?",
      signup: 'Sign up',
      fillFields: 'Please fill in all fields.',
      accountCreated: 'Account created. Welcome aboard!',
      welcomeBack: 'Welcome back!',
      genericError: 'Something went wrong. Please try again.',
      demoReady: 'Demo workspace ready.',
      demoError: 'Could not start demo.',
    },
    dashboard: {
      subGreeting: "Let's focus on what matters today.",
      mainFocusEyebrow: "Today's Main Focus",
      startNow: 'Start now',
      topPriorities: 'Top 3 Priorities',
      allTasks: 'All tasks',
      topPrioritiesEmpty: 'No open tasks. Enjoy a calm moment or add one.',
      noDate: 'No date',
      activeGoals: 'Active Goals',
      allGoals: 'All goals',
      goalsEmpty: 'No active goals yet.',
      projectsTitle: 'Projects Needing Attention',
      allProjects: 'All projects',
      projectsEmpty: 'Nothing needs attention right now.',
      financeTitle: 'Finance Snapshot',
      income: 'Income',
      expenses: 'Expenses',
      remaining: 'Remaining',
      monthlyIncomeGoal: 'Monthly income goal',
      habitsTitle: 'Habits Today',
      allHabits: 'All habits',
      habitsEmpty: 'No habits yet.',
      aiNextStep: 'AI Next Step',
      askAssistant: 'Ask the Assistant',
      loader: 'Preparing your day…',
      loadError: 'Could not load your dashboard.',
      taskDone: 'Nice. One step done.',
      taskError: 'Could not update task.',
      habitError: 'Could not update habit.',
    },
    goals: {
      title: 'Goals',
      subtitle: 'What you are building toward',
      emptyTitle: 'No goals here yet',
      emptySubtitle: 'Create a goal to give your days direction.',
      addFab: 'Add goal',
      loader: 'Loading goals…',
      loadError: 'Could not load goals.',
      steps: '{done}/{total} steps',
      deadline: 'Deadline: {date}',
      editTitle: 'Edit goal',
      newTitle: 'New goal',
      create: 'Create goal',
      fieldTitle: 'Title',
      titlePlaceholder: 'e.g. Build AI App Portfolio',
      category: 'Category',
      status: 'Status',
      description: 'Description',
      descriptionPlaceholder: 'What does success look like?',
      deadlineField: 'Deadline',
      progress: 'Progress · {n}%',
      actionSteps: 'Action steps',
      stepPlaceholder: 'Add an action step',
      complete: 'Complete',
      confirmTitle: 'Delete this goal?',
      titleRequired: 'Please add a goal title.',
      updated: 'Goal updated.',
      created: 'Goal created.',
      saveError: 'Could not save goal.',
      completed: 'Goal completed. Well done!',
      updateError: 'Could not update goal.',
      deleted: 'Goal deleted.',
      deleteError: 'Could not delete goal.',
    },
    tasks: {
      title: 'Tasks',
      subtitle: 'Focus on a few that matter today',
      emptyCompletedTitle: 'Nothing completed yet',
      emptyTitle: 'No tasks here',
      emptyTodaySubtitle: 'A calm day. Add a task or rest.',
      emptySubtitle: 'Add a task to plan ahead.',
      addFab: 'Add task',
      loader: 'Loading tasks…',
      loadError: 'Could not load tasks.',
      editTitle: 'Edit task',
      newTitle: 'New task',
      addTask: 'Add task',
      fieldTitle: 'Title',
      titlePlaceholder: 'e.g. Send 10 outreach messages',
      description: 'Description',
      descriptionPlaceholder: 'Optional details',
      priority: 'Priority',
      status: 'Status',
      dueDate: 'Due date',
      relatedGoal: 'Related goal',
      relatedProject: 'Related project',
      deleteTask: 'Delete task',
      confirmTitle: 'Delete this task?',
      titleRequired: 'Please add a task title.',
      updated: 'Task updated.',
      added: 'Task added.',
      saveError: 'Could not save task.',
      done: 'Done. Keep the momentum.',
      updateError: 'Could not update task.',
      deleted: 'Task deleted.',
      deleteError: 'Could not delete task.',
    },
    projects: {
      title: 'Projects',
      subtitle: 'Keep momentum on what matters',
      emptyTitle: 'No projects here',
      emptySubtitle: 'Create a project to organize your work.',
      addFab: 'Add project',
      loader: 'Loading projects…',
      loadError: 'Could not load projects.',
      editTitle: 'Edit project',
      newTitle: 'New project',
      create: 'Create project',
      name: 'Project name',
      namePlaceholder: 'e.g. Dubai 2026 Business Outreach',
      category: 'Category',
      status: 'Status',
      description: 'Description',
      descriptionPlaceholder: 'What is this project about?',
      nextAction: 'Next action',
      nextActionPlaceholder: 'The single next step',
      progress: 'Progress · {n}%',
      deleteProject: 'Delete project',
      confirmTitle: 'Delete this project?',
      confirmDesc: 'Linked tasks will be kept but unlinked.',
      nameRequired: 'Please add a project name.',
      updated: 'Project updated.',
      created: 'Project created.',
      saveError: 'Could not save project.',
      deleted: 'Project deleted.',
      deleteError: 'Could not delete project.',
    },
    projectDetail: {
      title: 'Project',
      notFoundTitle: 'Project not found',
      notFoundSubtitle: 'It may have been deleted.',
      nextActionLabel: 'Next action',
      nextActionEmpty: 'Define the single next step.',
      tasksHeading: 'Tasks',
      addTask: 'Add task',
      tasksEmptyTitle: 'No tasks yet',
      tasksEmptySubtitle: 'Break this project into small steps.',
      drawerTitle: 'Add task to project',
      fieldTitle: 'Title',
      titlePlaceholder: 'What needs to be done?',
      priority: 'Priority',
      loader: 'Loading project…',
      loadError: 'Could not load project.',
      updateError: 'Could not update task.',
      titleRequired: 'Please add a task title.',
      added: 'Task added to project.',
      addError: 'Could not add task.',
    },
    finance: {
      title: 'Finance',
      subtitle: 'Simple control over your money',
      monthlyGoal: 'Monthly Goal',
      income: 'Income',
      expenses: 'Expenses',
      remaining: 'Remaining',
      incomeGoalProgress: 'Income goal progress',
      nextMoneyAction: 'Next Money Action',
      recent: 'Recent transactions',
      emptyTitle: 'No transactions yet',
      emptySubtitle: 'Add income, an expense or a payment reminder.',
      addFab: 'Add transaction',
      loader: 'Loading finances…',
      loadError: 'Could not load finances.',
      drawerTitle: 'Add finance item',
      type: 'Type',
      fieldTitle: 'Title',
      titlePlaceholder: 'e.g. Client retainer',
      amount: 'Amount (PLN)',
      date: 'Date',
      confirmTitle: 'Delete this item?',
      invalid: 'Please add a title and a valid amount.',
      saved: 'Saved.',
      saveError: 'Could not save item.',
      deleted: 'Deleted.',
      deleteError: 'Could not delete item.',
    },
    habits: {
      title: 'Habits',
      subtitle: '{done}/{total} done today',
      today: 'Today',
      emptyTitle: 'No habits yet',
      emptySubtitle: 'Build momentum with one small daily habit.',
      addFab: 'Add habit',
      loader: 'Loading habits…',
      loadError: 'Could not load habits.',
      drawerTitle: 'New habit',
      name: 'Habit name',
      namePlaceholder: 'e.g. 30 minutes focused work',
      category: 'Category',
      frequency: 'Frequency',
      addHabit: 'Add habit',
      confirmTitle: 'Remove this habit?',
      confirmDesc: 'Your streak will be lost.',
      doneToday: '“{name}” done today.',
      updateError: 'Could not update habit.',
      nameRequired: 'Please add a habit name.',
      added: 'Habit added.',
      saveError: 'Could not save habit.',
      removed: 'Habit removed.',
      deleteError: 'Could not delete habit.',
    },
    ai: {
      title: 'AI Life Assistant',
      subtitle: 'Your in-app guide',
      prompt1: 'What should I focus on today?',
      prompt2: "I don't know where to start.",
      prompt3: 'I have too many tasks. Help me choose.',
      prompt4: 'Break my main goal into steps.',
      prompt5: 'What can this app help me with?',
      emptyTitle: 'How can I help you today?',
      orientation: 'I can help you decide what to do today, organize a goal, project or your tasks, or explain where to find things in Life Leverage OS.',
      emptySubtitle: 'I use the goals, tasks, projects, habits and finances already in your account. I never change anything myself.',
      mainPriority: 'Most important now',
      nextSteps: 'Next steps',
      clarifyLabel: 'Quick question',
      goTo: 'Go to',
      suggestedTask: 'Suggested task',
      add: 'Add',
      thinking: 'Thinking it through…',
      assistant: 'Assistant',
      composerPlaceholder: "Tell me what's on your mind…",
      loader: 'Waking the assistant…',
      loadError: 'Could not load conversation.',
      respondError: 'The assistant could not respond. Please try again.',
      taskAdded: 'Added to your tasks for today.',
      taskAddError: 'Could not add task.',
    },
    review: {
      title: 'Daily Review',
      subtitle: 'Close the day with clarity',
      tasksDone: 'Tasks done',
      habitsDone: 'Habits done',
      tomorrowLabel: "Tomorrow's suggested focus",
      qCompletedLabel: 'What did I complete today?',
      qCompletedPlaceholder: 'List what you finished…',
      qReflectionLabel: 'What moved my life forward?',
      qReflectionPlaceholder: 'Even one small thing counts…',
      qDifficultyLabel: 'What was difficult?',
      qDifficultyPlaceholder: 'Name it, then let it go…',
      qTomorrowLabel: 'What is one priority for tomorrow?',
      qTomorrowPlaceholder: 'Your single most important focus…',
      savedBanner: "Today's review is saved",
      update: 'Update review',
      save: 'Save review',
      loader: 'Loading your day…',
      loadError: 'Could not load your review.',
      saved: 'Review saved. Rest well.',
      saveError: 'Could not save review.',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Your profile & preferences',
      yourName: 'Your name',
      name: 'Name',
      email: 'Email',
      incomeGoal: 'Monthly income goal (PLN)',
      focus: 'Main life focus',
      focusPlaceholder: 'What are you building toward?',
      planning: 'Preferred daily planning style',
      planningPlaceholder: 'e.g. Focused deep work',
      notifications: 'Notifications',
      languageRow: 'Language',
      premiumTitle: 'Premium — coming soon',
      premiumText: 'Premium features are planned for a future version of Life Leverage OS. During closed testing, you can use the current system for goals, tasks, projects, habits, focus, finance overview and AI assistant.',
      premiumBadge: 'Coming soon',
      save: 'Save changes',
      logout: 'Log out',
      updated: 'Profile updated.',
      saveError: 'Could not save profile.',
    },
    enums: {
      // categories
      Business: 'Business',
      Finance: 'Finance',
      Health: 'Health',
      Personal: 'Personal',
      Learning: 'Learning',
      Relationships: 'Relationships',
      'Spiritual / Mindset': 'Spiritual / Mindset',
      'App Development': 'App Development',
      eBook: 'eBook',
      Sales: 'Sales',
      Work: 'Work',
      Mindset: 'Mindset',
      // goal/project statuses + filters
      All: 'All',
      Active: 'Active',
      'In Progress': 'In Progress',
      Paused: 'Paused',
      Completed: 'Completed',
      Planning: 'Planning',
      Waiting: 'Waiting',
      // task statuses + filters
      'To Do': 'To Do',
      Done: 'Done',
      Today: 'Today',
      Upcoming: 'Upcoming',
      // task priorities
      Low: 'Low',
      Medium: 'Medium',
      High: 'High',
      Critical: 'Critical',
      // finance types
      income: 'Income',
      expense: 'Expense',
      payment: 'Payment',
      // habit frequencies
      Daily: 'Daily',
      Weekdays: 'Weekdays',
      Weekly: 'Weekly',
    },
  },

  pl: {
    common: {
      save: 'Zapisz',
      saveChanges: 'Zapisz zmiany',
      cancel: 'Anuluj',
      delete: 'Usuń',
      remove: 'Usuń',
      edit: 'Edytuj',
      open: 'Otwórz',
      add: 'Dodaj',
      none: 'Brak',
      select: 'Wybierz',
      progress: 'Postęp',
      next: 'Następne:',
      cannotUndo: 'Tej operacji nie można cofnąć.',
      loading: 'Ładowanie…',
    },
    nav: {
      dashboard: 'Dziś',
      goals: 'Cele',
      tasks: 'Zadania',
      projects: 'Projekty',
      ai: 'Asystent',
    },
    more: {
      finance: 'Finanse',
      habits: 'Nawyki',
      review: 'Przegląd dnia',
      settings: 'Ustawienia',
    },
    screens: {
      dashboard: 'Dziś',
      goals: 'Cele',
      tasks: 'Zadania',
      projects: 'Projekty',
      finance: 'Finanse',
      habits: 'Nawyki',
      review: 'Przegląd dnia',
      settings: 'Ustawienia',
    },
    language: {
      label: 'Język',
      switch: 'Zmień język',
    },
    legal: {
      notice: '',
      backToApp: 'Back to app / Wróć do aplikacji',
      website: 'Website:',
      contact: 'Contact:',
    },
    onboarding: {
      title: 'Zamień chaos w jasny plan na dziś.',
      subtitle: 'Twoje osobiste centrum dowodzenia celami, zadaniami, finansami, nawykami i codziennym działaniem.',
      chipGoals: 'Cele',
      chipMoney: 'Finanse',
      chipHabits: 'Nawyki',
      getStarted: 'Zaczynamy',
      demo: 'Kontynuuj jako użytkownik demo',
      tagline: 'Spokój. Skupienie. Stworzone, by pokazać Ci następny krok.',
      demoWelcome: 'Witaj, Marek — Twoje demo jest gotowe.',
      demoError: 'Nie udało się uruchomić demo. Spróbuj ponownie.',
    },
    auth: {
      signupTitle: 'Załóż konto',
      loginTitle: 'Witaj ponownie',
      signupSubtitle: 'Zacznij porządkować swoje życie w jednym spokojnym miejscu.',
      loginSubtitle: 'Zaloguj się, aby kontynuować.',
      name: 'Imię',
      namePlaceholder: 'Twoje imię',
      email: 'E-mail',
      emailPlaceholder: 'you@example.com',
      password: 'Hasło',
      createAccount: 'Utwórz konto',
      login: 'Zaloguj się',
      or: 'lub',
      demo: 'Kontynuuj jako użytkownik demo',
      haveAccount: 'Masz już konto?',
      noAccount: 'Nie masz konta?',
      signup: 'Zarejestruj się',
      fillFields: 'Wypełnij wszystkie pola.',
      accountCreated: 'Konto utworzone. Witamy na pokładzie!',
      welcomeBack: 'Witaj ponownie!',
      genericError: 'Coś poszło nie tak. Spróbuj ponownie.',
      demoReady: 'Demo gotowe.',
      demoError: 'Nie udało się uruchomić demo.',
    },
    dashboard: {
      subGreeting: 'Skupmy się dziś na tym, co ważne.',
      mainFocusEyebrow: 'Główny cel na dziś',
      startNow: 'Zacznij teraz',
      topPriorities: '3 najważniejsze priorytety',
      allTasks: 'Wszystkie zadania',
      topPrioritiesEmpty: 'Brak otwartych zadań. Wykorzystaj chwilę spokoju lub dodaj nowe.',
      noDate: 'Bez daty',
      activeGoals: 'Aktywne cele',
      allGoals: 'Wszystkie cele',
      goalsEmpty: 'Brak aktywnych celów.',
      projectsTitle: 'Projekty wymagające uwagi',
      allProjects: 'Wszystkie projekty',
      projectsEmpty: 'Nic nie wymaga teraz uwagi.',
      financeTitle: 'Migawka finansów',
      income: 'Przychód',
      expenses: 'Wydatki',
      remaining: 'Pozostało',
      monthlyIncomeGoal: 'Miesięczny cel przychodu',
      habitsTitle: 'Nawyki na dziś',
      allHabits: 'Wszystkie nawyki',
      habitsEmpty: 'Brak nawyków.',
      aiNextStep: 'Następny krok AI',
      askAssistant: 'Zapytaj asystenta',
      loader: 'Przygotowuję Twój dzień…',
      loadError: 'Nie udało się załadować pulpitu.',
      taskDone: 'Świetnie. Jeden krok zrobiony.',
      taskError: 'Nie udało się zaktualizować zadania.',
      habitError: 'Nie udało się zaktualizować nawyku.',
    },
    goals: {
      title: 'Cele',
      subtitle: 'Do czego dążysz',
      emptyTitle: 'Nie ma tu jeszcze celów',
      emptySubtitle: 'Utwórz cel, aby nadać kierunek swoim dniom.',
      addFab: 'Dodaj cel',
      loader: 'Ładowanie celów…',
      loadError: 'Nie udało się załadować celów.',
      steps: '{done}/{total} kroków',
      deadline: 'Termin: {date}',
      editTitle: 'Edytuj cel',
      newTitle: 'Nowy cel',
      create: 'Utwórz cel',
      fieldTitle: 'Tytuł',
      titlePlaceholder: 'np. Zbuduj portfolio aplikacji AI',
      category: 'Kategoria',
      status: 'Status',
      description: 'Opis',
      descriptionPlaceholder: 'Jak wygląda sukces?',
      deadlineField: 'Termin',
      progress: 'Postęp · {n}%',
      actionSteps: 'Kroki działania',
      stepPlaceholder: 'Dodaj krok działania',
      complete: 'Ukończ',
      confirmTitle: 'Usunąć ten cel?',
      titleRequired: 'Dodaj tytuł celu.',
      updated: 'Cel zaktualizowany.',
      created: 'Cel utworzony.',
      saveError: 'Nie udało się zapisać celu.',
      completed: 'Cel ukończony. Brawo!',
      updateError: 'Nie udało się zaktualizować celu.',
      deleted: 'Cel usunięty.',
      deleteError: 'Nie udało się usunąć celu.',
    },
    tasks: {
      title: 'Zadania',
      subtitle: 'Skup się na kilku, które dziś się liczą',
      emptyCompletedTitle: 'Nic jeszcze nieukończone',
      emptyTitle: 'Brak zadań',
      emptyTodaySubtitle: 'Spokojny dzień. Dodaj zadanie lub odpocznij.',
      emptySubtitle: 'Dodaj zadanie, aby zaplanować z wyprzedzeniem.',
      addFab: 'Dodaj zadanie',
      loader: 'Ładowanie zadań…',
      loadError: 'Nie udało się załadować zadań.',
      editTitle: 'Edytuj zadanie',
      newTitle: 'Nowe zadanie',
      addTask: 'Dodaj zadanie',
      fieldTitle: 'Tytuł',
      titlePlaceholder: 'np. Wyślij 10 wiadomości do klientów',
      description: 'Opis',
      descriptionPlaceholder: 'Opcjonalne szczegóły',
      priority: 'Priorytet',
      status: 'Status',
      dueDate: 'Termin',
      relatedGoal: 'Powiązany cel',
      relatedProject: 'Powiązany projekt',
      deleteTask: 'Usuń zadanie',
      confirmTitle: 'Usunąć to zadanie?',
      titleRequired: 'Dodaj tytuł zadania.',
      updated: 'Zadanie zaktualizowane.',
      added: 'Zadanie dodane.',
      saveError: 'Nie udało się zapisać zadania.',
      done: 'Zrobione. Tak trzymaj.',
      updateError: 'Nie udało się zaktualizować zadania.',
      deleted: 'Zadanie usunięte.',
      deleteError: 'Nie udało się usunąć zadania.',
    },
    projects: {
      title: 'Projekty',
      subtitle: 'Utrzymuj tempo w tym, co ważne',
      emptyTitle: 'Brak projektów',
      emptySubtitle: 'Utwórz projekt, aby zorganizować pracę.',
      addFab: 'Dodaj projekt',
      loader: 'Ładowanie projektów…',
      loadError: 'Nie udało się załadować projektów.',
      editTitle: 'Edytuj projekt',
      newTitle: 'Nowy projekt',
      create: 'Utwórz projekt',
      name: 'Nazwa projektu',
      namePlaceholder: 'np. Dubaj 2026 — pozyskiwanie klientów',
      category: 'Kategoria',
      status: 'Status',
      description: 'Opis',
      descriptionPlaceholder: 'Czego dotyczy ten projekt?',
      nextAction: 'Następne działanie',
      nextActionPlaceholder: 'Jeden następny krok',
      progress: 'Postęp · {n}%',
      deleteProject: 'Usuń projekt',
      confirmTitle: 'Usunąć ten projekt?',
      confirmDesc: 'Powiązane zadania zostaną zachowane, ale odłączone.',
      nameRequired: 'Dodaj nazwę projektu.',
      updated: 'Projekt zaktualizowany.',
      created: 'Projekt utworzony.',
      saveError: 'Nie udało się zapisać projektu.',
      deleted: 'Projekt usunięty.',
      deleteError: 'Nie udało się usunąć projektu.',
    },
    projectDetail: {
      title: 'Projekt',
      notFoundTitle: 'Nie znaleziono projektu',
      notFoundSubtitle: 'Mógł zostać usunięty.',
      nextActionLabel: 'Następne działanie',
      nextActionEmpty: 'Określ jeden następny krok.',
      tasksHeading: 'Zadania',
      addTask: 'Dodaj zadanie',
      tasksEmptyTitle: 'Brak zadań',
      tasksEmptySubtitle: 'Podziel projekt na małe kroki.',
      drawerTitle: 'Dodaj zadanie do projektu',
      fieldTitle: 'Tytuł',
      titlePlaceholder: 'Co trzeba zrobić?',
      priority: 'Priorytet',
      loader: 'Ładowanie projektu…',
      loadError: 'Nie udało się załadować projektu.',
      updateError: 'Nie udało się zaktualizować zadania.',
      titleRequired: 'Dodaj tytuł zadania.',
      added: 'Zadanie dodane do projektu.',
      addError: 'Nie udało się dodać zadania.',
    },
    finance: {
      title: 'Finanse',
      subtitle: 'Prosta kontrola nad finansami',
      monthlyGoal: 'Cel miesięczny',
      income: 'Przychód',
      expenses: 'Wydatki',
      remaining: 'Pozostało',
      incomeGoalProgress: 'Postęp celu przychodu',
      nextMoneyAction: 'Następne działanie finansowe',
      recent: 'Ostatnie transakcje',
      emptyTitle: 'Brak transakcji',
      emptySubtitle: 'Dodaj przychód, wydatek lub przypomnienie o płatności.',
      addFab: 'Dodaj transakcję',
      loader: 'Ładowanie finansów…',
      loadError: 'Nie udało się załadować finansów.',
      drawerTitle: 'Dodaj pozycję finansową',
      type: 'Typ',
      fieldTitle: 'Tytuł',
      titlePlaceholder: 'np. Stała umowa z klientem',
      amount: 'Kwota (PLN)',
      date: 'Data',
      confirmTitle: 'Usunąć tę pozycję?',
      invalid: 'Dodaj tytuł i poprawną kwotę.',
      saved: 'Zapisano.',
      saveError: 'Nie udało się zapisać pozycji.',
      deleted: 'Usunięto.',
      deleteError: 'Nie udało się usunąć pozycji.',
    },
    habits: {
      title: 'Nawyki',
      subtitle: '{done}/{total} dziś wykonane',
      today: 'Dziś',
      emptyTitle: 'Brak nawyków',
      emptySubtitle: 'Buduj tempo jednym małym codziennym nawykiem.',
      addFab: 'Dodaj nawyk',
      loader: 'Ładowanie nawyków…',
      loadError: 'Nie udało się załadować nawyków.',
      drawerTitle: 'Nowy nawyk',
      name: 'Nazwa nawyku',
      namePlaceholder: 'np. 30 minut skupionej pracy',
      category: 'Kategoria',
      frequency: 'Częstotliwość',
      addHabit: 'Dodaj nawyk',
      confirmTitle: 'Usunąć ten nawyk?',
      confirmDesc: 'Twoja seria zostanie utracona.',
      doneToday: '„{name}” wykonane dziś.',
      updateError: 'Nie udało się zaktualizować nawyku.',
      nameRequired: 'Dodaj nazwę nawyku.',
      added: 'Nawyk dodany.',
      saveError: 'Nie udało się zapisać nawyku.',
      removed: 'Nawyk usunięty.',
      deleteError: 'Nie udało się usunąć nawyku.',
    },
    ai: {
      title: 'Asystent życia AI',
      subtitle: 'Twój przewodnik po aplikacji',
      prompt1: 'Co mam dziś zrobić?',
      prompt2: 'Nie wiem od czego zacząć.',
      prompt3: 'Mam za dużo zadań. Pomóż mi wybrać.',
      prompt4: 'Pomóż mi rozbić mój główny cel na kroki.',
      prompt5: 'Jak ta aplikacja może mi pomóc?',
      emptyTitle: 'W czym mogę Ci dziś pomóc?',
      orientation: 'Mogę pomóc Ci zdecydować, co zrobić dziś, uporządkować cel, projekt lub zadania albo wyjaśnić, gdzie coś znajdziesz w Life Leverage OS.',
      emptySubtitle: 'Korzystam z celów, zadań, projektów, nawyków i finansów, które już masz na koncie. Sam niczego nie zmieniam.',
      mainPriority: 'Najważniejsze teraz',
      nextSteps: 'Następne kroki',
      clarifyLabel: 'Krótkie pytanie',
      goTo: 'Przejdź do:',
      suggestedTask: 'Sugerowane zadanie',
      add: 'Dodaj',
      thinking: 'Zastanawiam się…',
      assistant: 'Asystent',
      composerPlaceholder: 'Powiedz, co masz na myśli…',
      loader: 'Budzę asystenta…',
      loadError: 'Nie udało się załadować rozmowy.',
      respondError: 'Asystent nie mógł odpowiedzieć. Spróbuj ponownie.',
      taskAdded: 'Dodano do Twoich zadań na dziś.',
      taskAddError: 'Nie udało się dodać zadania.',
    },
    review: {
      title: 'Przegląd dnia',
      subtitle: 'Zakończ dzień z jasnością',
      tasksDone: 'Zadania zrobione',
      habitsDone: 'Nawyki zrobione',
      tomorrowLabel: 'Sugerowany cel na jutro',
      qCompletedLabel: 'Co dziś ukończyłem?',
      qCompletedPlaceholder: 'Wypisz, co udało Ci się skończyć…',
      qReflectionLabel: 'Co popchnęło moje życie do przodu?',
      qReflectionPlaceholder: 'Nawet jedna mała rzecz się liczy…',
      qDifficultyLabel: 'Co było trudne?',
      qDifficultyPlaceholder: 'Nazwij to i odpuść…',
      qTomorrowLabel: 'Jaki jest jeden priorytet na jutro?',
      qTomorrowPlaceholder: 'Twój jeden najważniejszy cel…',
      savedBanner: 'Dzisiejszy przegląd jest zapisany',
      update: 'Zaktualizuj przegląd',
      save: 'Zapisz przegląd',
      loader: 'Ładowanie Twojego dnia…',
      loadError: 'Nie udało się załadować przeglądu.',
      saved: 'Przegląd zapisany. Odpocznij dobrze.',
      saveError: 'Nie udało się zapisać przeglądu.',
    },
    settings: {
      title: 'Ustawienia',
      subtitle: 'Twój profil i preferencje',
      yourName: 'Twoje imię',
      name: 'Imię',
      email: 'E-mail',
      incomeGoal: 'Miesięczny cel przychodu (PLN)',
      focus: 'Główny cel życiowy',
      focusPlaceholder: 'Do czego dążysz?',
      planning: 'Preferowany styl planowania dnia',
      planningPlaceholder: 'np. Skupiona, głęboka praca',
      notifications: 'Powiadomienia',
      languageRow: 'Język',
      premiumTitle: 'Premium — wkrótce',
      premiumText: 'Funkcje Premium są planowane w przyszłej wersji Life Leverage OS. Podczas testów zamkniętych możesz korzystać z obecnego systemu do celów, zadań, projektów, nawyków, skupienia, finansów i asystenta AI.',
      premiumBadge: 'Wkrótce',
      save: 'Zapisz zmiany',
      logout: 'Wyloguj się',
      updated: 'Profil zaktualizowany.',
      saveError: 'Nie udało się zapisać profilu.',
    },
    enums: {
      // categories
      Business: 'Biznes',
      Finance: 'Finanse',
      Health: 'Zdrowie',
      Personal: 'Osobiste',
      Learning: 'Nauka',
      Relationships: 'Relacje',
      'Spiritual / Mindset': 'Duchowość / Nastawienie',
      'App Development': 'Tworzenie aplikacji',
      eBook: 'eBook',
      Sales: 'Sprzedaż',
      Work: 'Praca',
      Mindset: 'Nastawienie',
      // goal/project statuses + filters
      All: 'Wszystkie',
      Active: 'Aktywne',
      'In Progress': 'W toku',
      Paused: 'Wstrzymane',
      Completed: 'Ukończone',
      Planning: 'Planowanie',
      Waiting: 'Oczekujące',
      // task statuses + filters
      'To Do': 'Do zrobienia',
      Done: 'Zrobione',
      Today: 'Dziś',
      Upcoming: 'Nadchodzące',
      // task priorities
      Low: 'Niski',
      Medium: 'Średni',
      High: 'Wysoki',
      Critical: 'Krytyczny',
      // finance types
      income: 'Przychód',
      expense: 'Wydatek',
      payment: 'Płatność',
      // habit frequencies
      Daily: 'Codziennie',
      Weekdays: 'Dni robocze',
      Weekly: 'Co tydzień',
    },
  },

  vi,
};

export const SUPPORTED_LANGS = ['en', 'pl', 'vi'];
